import {
  ChevronUp,
  ChevronDown,
  EditIcon,
  GoalIcon,
  Mail,
  Send,
  ExternalLink,
} from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GoalCard from './GoalCard';
import MessagesCard from './MessagesCard';
import ExitCard from './ExitCard';
import { ICampaign } from '../../../../types/campaigns/campaigns.types';

interface LeftSidePanelProps {
  campaign?: ICampaign;
  loading: boolean;
  error: string | null;
  onEditClick: () => void;
}

const LeftSidePanel = ({
  campaign,
  loading,
  error,
  onEditClick,
}: LeftSidePanelProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sidePanelName, setSidePanelName] = useState('');

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  // Show loading state - removed since we now show full canvas skeleton
  if (loading) {
    return null;
  }

  // Show error state
  if (error) {
    return (
      <div className="w-80 bg-card border border-border shadow-sm p-4 space-y-3 rounded-lg">
        <div className="text-destructive text-sm">
          Error loading campaign: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full flex ">
        {/* Campaign Header */}
        <div
          className={`w-80 bg-card border border-border shadow-sm p-4 space-y-3 
                    ${sidePanelName !== '' ? 'rounded-l-lg rounded-b-lg' : 'rounded-lg'}
                    `}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3
                className="text-lg font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                onClick={onEditClick}
                title="Click to edit title"
              >
                {campaign?.name || 'Untitled Campaign'}
              </h3>
              {campaign?.status === 'live' && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span className="text-xs text-success font-medium">
                    active
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={toggleCollapse}
              className="text-muted-foreground hover:text-foreground"
            >
              {isCollapsed ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Show campaign info only when collapsed */}
          {isCollapsed && (
            <div className="flex items-center gap-6 text-muted-foreground text-sm">
              <div className="flex items-center gap-1">
                <GoalIcon className="w-4 h-4" />
                <span>
                  {campaign?.goal?.type === 'goal' ? 'Has goal' : 'No goal'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                <span>
                  {campaign?.subscriptionPreferences || 'All subscribers'}
                </span>
              </div>
            </div>
          )}

          {/* Collapse Content with Smooth Transition */}
          <AnimatePresence initial={false}>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div
                  className="flex items-center space-x-2 text-muted-foreground cursor-pointer hover:text-primary transition-colors"
                  onClick={onEditClick}
                  title="Click to edit description"
                >
                  <span className="text-sm">
                    {campaign?.description || 'Add a description...'}
                  </span>
                  <EditIcon className="w-4 h-4" />
                </div>

                {campaign?.tags && campaign.tags.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {campaign.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <button className="mt-2 flex items-center space-x-1 px-3 py-1 border border-border rounded text-sm text-foreground hover:bg-muted">
                    <span>Add tag +</span>
                  </button>
                )}

                {/* Separator */}
                <div className="border-t border-border my-4"></div>

                {/* Goal Section */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-muted rounded flex items-center justify-center">
                      <GoalIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <span className="font-medium text-foreground">Goal</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Goals help you measure the success of your campaign.
                  </p>
                  <button
                    className="w-full bg-primary text-primary-foreground py-2 px-4 rounded text-sm font-medium hover:bg-primary-hover"
                    onClick={() => {
                      setSidePanelName('goal');
                    }}
                  >
                    Set goal
                  </button>
                </div>

                <div className="border-t border-border my-4"></div>

                {/* Messages Section */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-success-background rounded flex items-center justify-center">
                      <Send className="w-3 h-3 text-success" />
                    </div>
                    <span className="font-medium text-foreground">
                      Messages
                    </span>
                  </div>
                  <ul className="text-sm text-muted-foreground">
                    <li>• Don't count towards message limit</li>
                  </ul>
                  <button
                    className="w-full border border-border text-foreground py-2 px-4 rounded text-sm font-medium hover:bg-muted"
                    onClick={() => {
                      setSidePanelName('messages');
                    }}
                  >
                    Manage
                  </button>
                </div>

                <div className="border-t border-border my-4"></div>

                {/* Exit Section */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-success-background rounded flex items-center justify-center">
                      <ExternalLink className="w-3 h-3 text-success" />
                    </div>
                    <span className="font-medium text-foreground">Exit</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    A person will exit the campaign early when they stop
                    matching the trigger.
                  </p>
                  <button
                    className="w-full border border-border text-foreground py-2 px-4 rounded text-sm font-medium hover:bg-muted"
                    onClick={() => {
                      setSidePanelName('exit');
                    }}
                  >
                    Manage
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {sidePanelName === 'goal' && (
          <GoalCard onClose={() => setSidePanelName('')} />
        )}
        {sidePanelName === 'messages' && (
          <MessagesCard onClose={() => setSidePanelName('')} />
        )}
        {sidePanelName === 'exit' && (
          <ExitCard onClose={() => setSidePanelName('')} />
        )}
      </div>
    </div>
  );
};

export default LeftSidePanel;
